import React, { useState, useRef, useEffect } from "react";
import { useConfig } from "../../../../hooks/useConfig";
import { useTV } from "../../../../hooks/useTV";
import { useStreamTime } from "../../../../hooks/useStreamTime";
import useGlobalStore from "../../../../store/global.store";
import { useRemote } from "../../../../hooks/useRemote";
import OffStreamView from "../OffStreamView";
import { FaMapMarkerAlt } from "react-icons/fa";
import "./VipSkyline.styles.css";
import VipSubCheck from "../VipSubCheck";

const VipSkyline = () => {
  const { config } = useConfig();
  const { isTv } = useTV();
  const { loading, showStream, showStreamLoader, getShowTimeText } =
    useStreamTime();

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  // const setShowVipSelector = useGlobalStore(
  //   (state) => state.setShowVipSelector
  // );
  const setSelectedVipDoor = useGlobalStore(
    (state) => state.setSelectedVipDoor
  );
  const { updateView } = useRemote();
  // const showVipSelector = useGlobalStore((state) => state.showVipSelector);

  const [showParks, setShowParks] = useState(false);
  const [focusedButton, setFocusedButton] = useState("pin");

  const pinButtonRef = useRef(null);
  const exitButtonRef = useRef(null);

  // Conditionally apply TV-specific styles
  const topBarStyle = {
    height: isTv ? "130px" : "100px",
    background: "#12243F",
    width: "100vw",
    position: "absolute",
    left: 0,
    top: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    zIndex: 20,
    fontFamily: "Lato",
  };

  const fireworksShowTextStyle = {
    fontSize: isTv ? "40px" : "30px",
  };

  const liveLogoStyle = {
    position: "absolute",
    top: "100px",
    right: "0px",
    width: isTv ? "580px" : "340px",
    zIndex: 20,
  };

  const balconyStyle = {
    position: "absolute",
    bottom: isTv ? "-35px" : "0",
    left: 0,
    width: "100vw",
    zIndex: 20,
  };

  // Common button styles
  const buttonSize = isTv ? "80px" : "60px";
  const buttonFontSize = isTv ? "24px" : "18px";

  // Button position styles
  const pinButtonStyle = {
    width: buttonSize,
    height: buttonSize,
    fontSize: buttonFontSize,
    left: "30px",
    bottom: "180px",
  };

  const exitButtonStyle = {
    width: buttonSize,
    height: buttonSize,
    fontSize: buttonFontSize,
    left: "30px",
    bottom: "70px",
  };

  const toggleParks = () => {
    setShowParks((prevState) => !prevState);
  };

  // Modified handler functions to ensure consistent toggling
  const handlePinClick = () => {
    toggleParks();
  };

  const handleKeyboardAction = (buttonType) => {
    if (buttonType === "pin") {
      toggleParks();
    } else if (buttonType === "exit") {
      handleExit();
    }
  };

  const handleExit = () => {
    setSelectedVipDoor("door2");
    updateView("VIP_door_2_selector", () => {});
  };

  // Auto-focus pin button when component mounts (for TV mode)
  useEffect(() => {
    setShowBottomBar(false);
    if (isTv && pinButtonRef.current) {
      pinButtonRef.current.focus();
      setFocusedButton("pin");
    }
  }, [isTv]);

  // Keyboard navigation for TV mode
  useEffect(() => {
    if (isTv) {
      const handleKeyDown = (e) => {
        const buttons = ["pin", "exit"];
        const currentIndex = buttons.indexOf(focusedButton);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          // Navigate to next button or wrap to first
          const nextIndex = (currentIndex + 1) % buttons.length;
          const nextButton = buttons[nextIndex];
          setFocusedButton(nextButton);

          // Focus the appropriate button
          if (nextButton === "pin" && pinButtonRef.current) {
            pinButtonRef.current.focus();
          } else if (nextButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          // Navigate to previous button or wrap to last
          const prevIndex =
            (currentIndex - 1 + buttons.length) % buttons.length;
          const prevButton = buttons[prevIndex];
          setFocusedButton(prevButton);

          // Focus the appropriate button
          if (prevButton === "pin" && pinButtonRef.current) {
            pinButtonRef.current.focus();
          } else if (prevButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "Enter") {
          e.preventDefault();
          handleKeyboardAction(focusedButton);
        }
      };

      // Add event listener
      window.addEventListener("keydown", handleKeyDown);

      // Clean up
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, focusedButton]);

  // Helper function to apply focus styling
  const getFocusStyle = (buttonId) => {
    if (isTv && focusedButton === buttonId) {
      return {
        outline: "none",
        boxShadow: "0 0 0 4px #3ADF3C",
      };
    }
    return {};
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontSize: "30px",
            color: "blue",
            fontFamily: "Lato",
            zIndex: 50,
          }}
        >
          <span>Loading...</span>
        </div>
      ) : (
        <>
          {showStream === true ? (
            <>
              <div
                style={{
                  position: "absolute",
                  top: showStreamLoader ? 0 : "-100vh",
                  transition: showStreamLoader ? "none" : "top 3s",
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 60,
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                  }}
                  src="/static/curtainRaiseUSE.jpg"
                  alt=""
                />
              </div>

              <div style={topBarStyle}>
                <span style={fireworksShowTextStyle}>
                  NEXT FIREWORKS SHOW: {getShowTimeText()}
                </span>
              </div>

              <iframe
                title="Stream"
                style={{
                  width: "230vw",
                  height: "100vw",
                  zIndex: 10,
                  position: "absolute",
                  top: 180,
                  right: isTv ? -300 : -190,
                  transform: "rotate(-6deg) scale(1.4)",
                }}
                src={
                  config.streamUrl === 1
                    ? "https://live2.mywaitserver.com/live/player/"
                    : "https://live.mywaitserver.com/live/player"
                }
                allowFullScreen=""
                frameBorder="0"
              />

              <img
                src="/static/stream/livelogo.png"
                alt=""
                style={liveLogoStyle}
              />

              {/* Pin button */}
              <button
                ref={pinButtonRef}
                className={`stream-round-button ${showParks ? "on" : "off"}`}
                onClick={handlePinClick}
                onFocus={() => setFocusedButton("pin")}
                style={{
                  position: isTv ? undefined : "absolute",
                  top: isTv ? undefined : "74%",
                  ...pinButtonStyle,
                  ...getFocusStyle("pin"),
                }}
                tabIndex={0}
                aria-label="Toggle Map Pins"
              >
                <FaMapMarkerAlt size={isTv ? 32 : 24} />
              </button>

              {/* Exit button */}
              <button
                ref={exitButtonRef}
                className="stream-round-button"
                onClick={handleExit}
                onFocus={() => setFocusedButton("exit")}
                style={{
                  ...exitButtonStyle,
                  ...getFocusStyle("exit"),
                }}
                tabIndex={0}
                aria-label="Exit"
              >
                <span>Exit</span>
              </button>

              <img
                src="/static/stream/balcony.png"
                alt=""
                style={balconyStyle}
              />

              {showParks && (
                <>
                  <img
                    src="/static/stream/pin%20map.png"
                    alt=""
                    style={{
                      position: "absolute",
                      top: "310px",
                      left: isTv ? "180px" : "200px",
                      width: isTv ? "250px" : "120px",
                      zIndex: 20,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: isTv ? "400px" : "350px",
                      left: isTv ? "30px" : "110px",
                      width: "fit-content",
                      zIndex: 20,
                      backgroundColor: "white",
                      padding: "0px 20px",
                      border: "6px solid #12243F",
                      fontFamily: "Lato",
                      fontWeight: "800",
                      fontSize: isTv ? "55px" : "25px",
                      borderRadius: "10px",
                    }}
                  >
                    EPCOT
                  </div>

                  <img
                    src="/static/stream/pin%20map.png"
                    alt=""
                    style={{
                      position: "absolute",
                      top: isTv ? "355px" : "335px",
                      left: isTv ? "300px" : "285px",
                      width: isTv ? "250px" : "120px",
                      zIndex: 20,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: isTv ? "450px" : "380px",
                      left: isTv ? "490px" : "370px",
                      width: "fit-content",
                      zIndex: 20,
                      backgroundColor: "white",
                      padding: "0px 20px",
                      border: "6px solid #12243F",
                      fontFamily: "Lato",
                      fontWeight: "800",
                      fontSize: isTv ? "55px" : "25px",
                      borderRadius: "10px",
                    }}
                  >
                    Disney Springs
                  </div>

                  <img
                    src="/static/stream/pin%20map.png"
                    alt=""
                    style={{
                      position: "absolute",
                      top: isTv ? "320px" : "300px",
                      right: isTv ? "175px" : "75px",
                      width: isTv ? "250px" : "120px",
                      zIndex: 20,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: isTv ? "410px" : "340px",
                      right: isTv ? "365px" : "165px",
                      width: "fit-content",
                      zIndex: 20,
                      backgroundColor: "white",
                      padding: "0px 20px",
                      border: "6px solid #12243F",
                      fontFamily: "Lato",
                      fontWeight: "800",
                      fontSize: isTv ? "55px" : "25px",
                      borderRadius: "10px",
                    }}
                  >
                    Magic Kingdom
                  </div>
                </>
              )}
            </>
          ) : (
            showStream === false && <OffStreamView />
          )}
        </>
      )}
      <VipSubCheck />
    </div>
  );
};

export default VipSkyline;
